/**
 * Gastronomia específica pedida pelo utilizador (módulo puro — sem IA, sem
 * Maps, sem créditos).
 *
 * Regra genérica e multilíngue: quando o texto nomeia uma especialidade
 * concreta ("pastel de nata", "doces regionais", "gelato artigianale"), essa
 * especialidade passa a ser um REQUISITO do roteiro, não um enfeite. O módulo
 * só extrai e descreve a pesquisa — a validação real é sempre feita com
 * resultados do Places, nunca inventada.
 */

export type SpecialtyVenueKind = "restaurant" | "bakery" | "cafe" | "market" | "icecream";

export type LocalSpecialty = {
  /** Termo tal como aparece no pedido (normalizado). */
  term: string;
  /** Consulta canónica a usar nas pesquisas. */
  query: string;
  /** Tipos de estabelecimento que servem esta especialidade, por prioridade. */
  kinds: SpecialtyVenueKind[];
  /** True quando o utilizador nomeou o prato/doce concreto. */
  explicit: boolean;
};

export type SignatureSpecialtyVenue = {
  query: string;
  venue: string;
  kinds: SpecialtyVenueKind[];
};

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase();

/**
 * Estabelecimentos de referência que fazem parte do próprio nome popular da
 * especialidade. O catálogo é global e orientado a dados: os consumidores não
 * precisam de regras por cidade e a paragem passa a ser vinculativa.
 */
const SIGNATURE_SPECIALTY_VENUES: Array<SignatureSpecialtyVenue & { re: RegExp }> = [
  {
    re: /\bpast[eé]is?\s+de\s+bel[ée]m\b|\bpastel\s+de\s+bel[ée]m\b/iu,
    query: "Pastéis de Belém",
    venue: "Pastéis de Belém",
    kinds: ["bakery", "cafe"],
  },
];

export function extractSignatureSpecialtyVenues(text: string): SignatureSpecialtyVenue[] {
  return SIGNATURE_SPECIALTY_VENUES.filter((entry) => entry.re.test(text ?? "")).map(
    ({ query, venue, kinds }) => ({ query, venue, kinds: [...kinds] }),
  );
}

/**
 * Especialidades nomeadas. A lista é um dicionário de termos — não contém
 * regras por cidade nem por país: qualquer destino usa o mesmo mecanismo.
 */
const NAMED_SPECIALTIES: Array<{
  re: RegExp;
  query: string;
  kinds: SpecialtyVenueKind[];
}> = [
  {
    re: /\bpasteis?\s+de\s+nata\b|\bpastel\s+de\s+nata\b|\bpastel\s+de\s+belem\b/u,
    query: "pastel de nata",
    kinds: ["bakery", "cafe"],
  },
  {
    re: /\bpasteis?\s+de\s+bacalhau\b|\bbolinhos?\s+de\s+bacalhau\b/u,
    query: "pastéis de bacalhau",
    kinds: ["restaurant", "bakery"],
  },
  { re: /\bfrancesinhas?\b/u, query: "francesinha", kinds: ["restaurant"] },
  { re: /\bbifanas?\b/u, query: "bifana", kinds: ["restaurant", "cafe"] },
  { re: /\bovos\s+moles\b/u, query: "ovos moles", kinds: ["bakery"] },
  {
    re: /\bqueijadas?\b|\btravesseiros?\b|\bpasteis?\s+de\s+tentugal\b/u,
    query: "doçaria conventual",
    kinds: ["bakery", "cafe"],
  },
  {
    re: /\bginjinha\b|\bvinho\s+do\s+porto\b|\bporto\s+wine\b/u,
    query: "prova de vinhos",
    kinds: ["market", "restaurant"],
  },
  {
    re: /\bdoces?\s+(?:regionais?|tipicos?|tradicionais?|conventuais?)\b|\bregional\s+sweets?\b|\btraditional\s+pastr(?:y|ies)\b/u,
    query: "doces regionais",
    kinds: ["bakery", "cafe"],
  },
  {
    re: /\bgelato\b|\bgelateria\b|\bsorvet\w*\b|\bice\s*cream\b|\bgelad[oa]s?\s+artesan\w*/u,
    query: "gelato artesanal",
    kinds: ["icecream", "cafe"],
  },
  { re: /\bcroissants?\b|\bviennoiseries?\b/u, query: "croissant", kinds: ["bakery", "cafe"] },
  { re: /\bmacarons?\b/u, query: "macaron", kinds: ["bakery"] },
  { re: /\btapas\b|\bpinchos?\b|\bpintxos?\b/u, query: "tapas", kinds: ["restaurant", "market"] },
  { re: /\bpaella\b/u, query: "paella", kinds: ["restaurant"] },
  { re: /\bchurros?\b/u, query: "churros", kinds: ["bakery", "cafe"] },
  { re: /\bjamon\s+iberico\b|\bpresunto\b/u, query: "presunto", kinds: ["market", "restaurant"] },
  {
    re: /\bpizza\s+napoletana\b|\bpizza\s+napolitana\b/u,
    query: "pizza napoletana",
    kinds: ["restaurant"],
  },
  { re: /\bcicchetti\b|\baperitivo\b/u, query: "aperitivo", kinds: ["restaurant", "cafe"] },
  { re: /\bsachertorte\b|\bapfelstrudel\b/u, query: "Sachertorte", kinds: ["cafe", "bakery"] },
  { re: /\bbratwurst\b|\bcurrywurst\b/u, query: "wurst", kinds: ["restaurant", "market"] },
  { re: /\bfondue\b|\braclette\b/u, query: "fondue", kinds: ["restaurant"] },
  {
    re: /\bcroque\s+monsieur\b|\bcrepes?\b|\bgalettes?\b/u,
    query: "crêpe",
    kinds: ["restaurant", "cafe"],
  },
  { re: /\bsushi\b|\bramen\b|\bizakaya\b/u, query: "cozinha japonesa", kinds: ["restaurant"] },
  { re: /\bfish\s+and\s+chips\b/u, query: "fish and chips", kinds: ["restaurant"] },
  {
    re: /\bwhisk(?:y|ey)\s+(?:tasting|prova|distiller\w*)\b|\bdestilaria\b/u,
    query: "destilaria",
    kinds: ["market", "restaurant"],
  },
];

/**
 * Verbos de intenção ("quero provar X", "I want to try X"). Usados para
 * capturar especialidades que não estão no dicionário, mantendo o módulo
 * genérico para qualquer destino.
 */
const INTENT_PATTERNS: RegExp[] = [
  /\b(?:quero|queria|gostava\s+de|gostaria\s+de|adorava)\s+(?:provar|comer|experimentar)\s+(?:o\s+|a\s+|os\s+|as\s+|um\s+|uma\s+)?([a-z0-9\s'-]{3,40})/gu,
  /\b(?:i\s+want|i'd\s+like|we\s+want)\s+to\s+(?:try|taste|eat)\s+(?:the\s+|some\s+|a\s+)?([a-z0-9\s'-]{3,40})/gu,
  /\b(?:quiero|queremos)\s+(?:probar|comer)\s+(?:el\s+|la\s+|los\s+|las\s+|un\s+|una\s+)?([a-z0-9\s'-]{3,40})/gu,
  /\b(?:je\s+veux|j'aimerais|nous\s+voulons)\s+(?:gouter|manger|essayer)\s+(?:le\s+|la\s+|les\s+|du\s+|des\s+|un\s+|une\s+)?([a-z0-9\s'-]{3,40})/gu,
  /\b(?:voglio|vorrei|vogliamo)\s+(?:provare|assaggiare|mangiare)\s+(?:il\s+|la\s+|le\s+|un\s+|una\s+)?([a-z0-9\s'-]{3,40})/gu,
  /\b(?:ich\s+moechte|wir\s+moechten)\s+([a-z0-9\s'-]{3,40})\s+(?:probieren|essen)/gu,
];

/** Palavras que nunca são uma especialidade por si só. */
const STOP_TERMS = new Set([
  "gastronomia",
  "comida",
  "food",
  "cuisine",
  "essen",
  "restaurantes",
  "restaurante",
  "restaurants",
  "coisas",
  "algo",
  "tudo",
  "local",
  "typical",
  "tipica",
  "tipico",
]);

/** Preposições de lugar que terminam a especialidade ("pastéis ... em Lisboa"). */
const PLACE_TAIL =
  /\s+(?:em|no|na|nos|nas|in|at|en|dans|nel|nella|im|bei)\s+[a-z].*$/u;

function cleanTerm(raw: string): string {
  return raw
    .replace(
      /\b(?:local|tipic\w*|tradicional|traditional|autentic\w*|bom|boa|melhor|best)\b/gu,
      " ",
    )
    .replace(/\s{2,}/gu, " ")
    .replace(PLACE_TAIL, "")
    .replace(/[\s,.;:!?-]+$/u, "")
    .trim();
}

/** Extrai especialidades gastronómicas concretas do texto do pedido. */
export function extractLocalSpecialties(text: string): LocalSpecialty[] {
  const folded = fold(text ?? "");
  if (!folded.trim()) return [];
  const out: LocalSpecialty[] = [];
  const seen = new Set<string>();

  for (const entry of NAMED_SPECIALTIES) {
    const m = folded.match(entry.re);
    if (!m) continue;
    if (seen.has(entry.query)) continue;
    seen.add(entry.query);
    out.push({ term: m[0].trim(), query: entry.query, kinds: entry.kinds, explicit: true });
  }

  for (const pattern of INTENT_PATTERNS) {
    pattern.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = pattern.exec(folded))) {
      const term = cleanTerm(m[1] ?? "");
      if (term.length < 3) continue;
      if (STOP_TERMS.has(term)) continue;
      if (out.some((s) => term.includes(fold(s.query)) || fold(s.query).includes(term))) continue;
      if (seen.has(term)) continue;
      seen.add(term);
      out.push({
        term,
        query: term,
        kinds: ["restaurant", "bakery", "cafe", "market"],
        explicit: true,
      });
    }
  }

  return out;
}

/**
 * Consultas de pesquisa determinísticas para uma especialidade numa cidade.
 * Ordem: termo + cidade → termo + tipo → tipo genérico (fallback local).
 */
export function specialtySearchQueries(specialty: LocalSpecialty, city: string): string[] {
  const place = (city ?? "").trim();
  const queries = new Set<string>();
  if (place) queries.add(`${specialty.query} ${place}`);
  for (const kind of specialty.kinds) {
    queries.add(place ? `${specialty.query} ${kind} ${place}` : `${specialty.query} ${kind}`);
  }
  if (place) queries.add(`${specialty.kinds[0]} ${place}`);
  return [...queries];
}

/**
 * Distribui as especialidades pelas cidades de um roteiro multi-cidade, sem
 * repetir a mesma especialidade em dias seguidos quando há alternativas.
 */
export function distributeSpecialties(
  specialties: readonly LocalSpecialty[],
  cities: readonly string[],
): Array<{ city: string; specialty: LocalSpecialty }> {
  const list = cities.filter((c) => (c ?? "").trim().length > 0);
  if (!specialties.length || !list.length) return [];
  return list.map((city, index) => ({
    city,
    specialty: specialties[index % specialties.length],
  }));
}

/**
 * Instrução determinística a juntar ao pedido enviado à IA. Não inventa
 * estabelecimentos: obriga a validar cada sugestão com um resultado real.
 */
export function specialtyPromptDirective(
  specialties: readonly LocalSpecialty[],
  cities: readonly string[] = [],
): string {
  if (!specialties.length) return "";
  const names = specialties.map((s) => s.query).join(", ");
  const plan = distributeSpecialties(specialties, cities)
    .map(({ city, specialty }) => `${city}: ${specialty.query}`)
    .join("; ");
  const lines = [
    `Gastronomia obrigatória do pedido: ${names}.`,
    "Cada uma tem de ter pelo menos uma paragem real e verificável no roteiro.",
    "Nunca inventes o nome de um estabelecimento: usa apenas locais confirmados.",
  ];
  if (plan) lines.push(`Distribuição sugerida por cidade — ${plan}.`);
  return lines.join(" ");
}

/** Etiqueta honesta quando a especialidade não pôde ser confirmada. */
export function specialtyDisclaimer(language = "pt"): string {
  const map: Record<string, string> = {
    pt: "Especialidade a confirmar no local.",
    en: "Specialty to confirm on site.",
    es: "Especialidad por confirmar en el lugar.",
    fr: "Spécialité à confirmer sur place.",
    it: "Specialità da confermare sul posto.",
    de: "Spezialität vor Ort zu bestätigen.",
  };
  return map[(language || "pt").slice(0, 2).toLowerCase()] ?? map.en;
}

/** As especialidades só valem se as refeições não estiverem desligadas. */
export function specialtiesAllowed(mealPreference: string | null | undefined): boolean {
  return mealPreference !== "none";
}

/** Palavra genérica do estabelecimento que serve cada tipo de especialidade. */
const KIND_VENUE_WORD: Record<SpecialtyVenueKind, string> = {
  bakery: "pastelaria",
  cafe: "café",
  restaurant: "restaurante",
  market: "mercado",
  icecream: "gelataria",
};

/**
 * Consulta única a usar como paragem obrigatória quando o pedido menciona uma
 * especialidade (mesmo genérica, como "um doce típico"). Garante que o motor
 * procura um estabelecimento REAL em vez de depender só da IA.
 * Genérico para qualquer localidade do mundo.
 */
export function specialtyStopQuery(
  specialties: readonly LocalSpecialty[],
  city?: string,
): string | undefined {
  const specialty = specialties[0];
  if (!specialty) return undefined;
  const venue = KIND_VENUE_WORD[specialty.kinds[0] ?? "bakery"] ?? "";
  const place = (city ?? "").trim();
  return [specialty.query, venue, place].filter(Boolean).join(" ").slice(0, 100);
}
